import React from "react";
import { ShieldAlert } from "lucide-react";

/**
 * RadialProgress — Concentric dual-ring SVG progress indicator.
 *
 *   Outer ring → lessons completed %
 *   Inner ring → chapters completed %
 *
 * When `flagged > 0`, a small red badge appears under the ring listing
 * the integrity-violation count. The component degrades gracefully when
 * totals are zero (shows 0% rather than NaN).
 *
 * Props:
 *   lessonsCompleted, totalLessons   — outer ring numerator / denominator
 *   chaptersCompleted, totalChapters — inner ring numerator / denominator
 *   flagged                          — number of flagged quiz attempts
 *   size                             — pixel diameter of the ring (default 132)
 *   label                            — optional caption above the ring
 *   compact                          — hide the legend underneath
 */
const clampPct = (num, denom) => {
  if (!denom || denom <= 0) return 0;
  const v = Math.round((num / denom) * 100);
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
};

export function RadialProgress({
  lessonsCompleted = 0,
  totalLessons = 0,
  chaptersCompleted = 0,
  totalChapters = 0,
  flagged = 0,
  size = 132,
  label,
  compact = false,
  testId = "radial-progress",
}) {
  const lessonsPct = clampPct(lessonsCompleted, totalLessons);
  const chaptersPct = clampPct(chaptersCompleted, totalChapters);

  // Concentric ring geometry. Both rings share the same centre.
  const stroke = Math.max(6, Math.round(size * 0.085));
  const center = size / 2;
  const outerR = center - stroke / 2 - 2;
  const innerR = outerR - stroke - 6;
  const outerC = 2 * Math.PI * outerR;
  const innerC = 2 * Math.PI * innerR;

  const outerDash = outerC * (lessonsPct / 100);
  const innerDash = innerC * (chaptersPct / 100);

  return (
    <div
      className="flex flex-col items-center gap-2"
      data-testid={testId}
    >
      {label ? (
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      ) : null}
      <div
        className="relative"
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
          aria-hidden="true"
        >
          {/* Outer track */}
          <circle
            cx={center}
            cy={center}
            r={outerR}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-slate-200 dark:text-slate-700/60"
          />
          {/* Outer progress (lessons) */}
          <circle
            cx={center}
            cy={center}
            r={outerR}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${outerDash} ${outerC - outerDash}`}
            className="text-sky-500 transition-[stroke-dasharray] duration-700 ease-out"
          />
          {/* Inner track */}
          <circle
            cx={center}
            cy={center}
            r={innerR}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-slate-200 dark:text-slate-700/40"
          />
          {/* Inner progress (chapters) */}
          <circle
            cx={center}
            cy={center}
            r={innerR}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${innerDash} ${innerC - innerDash}`}
            className="text-amber-500 transition-[stroke-dasharray] duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span
            className="text-2xl font-semibold leading-none tracking-tight"
            data-testid={`${testId}-lessons-pct`}
          >
            {lessonsPct}%
          </span>
          <span className="mt-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            Lessons
          </span>
        </div>
      </div>

      {!compact ? (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-sky-500" />
            Lessons {lessonsCompleted}/{totalLessons || 0}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            Chapters {chaptersCompleted}/{totalChapters || 0} ({chaptersPct}%)
          </span>
        </div>
      ) : null}

      {flagged > 0 ? (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:bg-red-500/15 dark:text-red-300"
          data-testid={`${testId}-flagged-badge`}
        >
          <ShieldAlert className="h-3 w-3" />
          {flagged} flagged attempt{flagged === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}

export default RadialProgress;
